export type InvoiceLineSnapshot = {
  kind: string;
  name: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  subtotal: number;
};

export type InvoiceChangeType = 'added' | 'removed' | 'edited';

export type InvoiceLineChange<T extends InvoiceLineSnapshot = InvoiceLineSnapshot> = {
  id: string;
  type: InvoiceChangeType;
  kind: string;
  before: T | null;
  after: T | null;
  beforeText: string;
  afterText: string;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function formatInvoiceLineSnapshot(line: InvoiceLineSnapshot | null | undefined): string {
  if (!line) return '';
  const desc = line.description?.trim() ? ` — ${line.description.trim()}` : '';
  return `${line.name}${desc} · ${round2(line.quantity)} × ${round2(line.unitPrice)} = ${round2(line.subtotal)}`;
}

function sameIdentity(a: InvoiceLineSnapshot, b: InvoiceLineSnapshot): boolean {
  return a.kind === b.kind && a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
}

function sameContent(a: InvoiceLineSnapshot, b: InvoiceLineSnapshot): boolean {
  return (
    sameIdentity(a, b) &&
    round2(a.quantity) === round2(b.quantity) &&
    round2(a.unitPrice) === round2(b.unitPrice) &&
    (a.description || '').trim() === (b.description || '').trim()
  );
}

/** Diferencias determinísticas entre el cálculo default y las líneas actuales. */
export function diffInvoiceLines<T extends InvoiceLineSnapshot>(
  before: T[],
  after: T[]
): InvoiceLineChange<T>[] {
  const afterUsed = new Set<number>();
  const matched: { b: T; a: T }[] = [];
  const removed: T[] = [];

  for (const b of before) {
    const idx = after.findIndex((a, i) => !afterUsed.has(i) && sameIdentity(b, a));
    if (idx >= 0) {
      afterUsed.add(idx);
      matched.push({ b, a: after[idx] });
    } else {
      removed.push(b);
    }
  }

  const added = after.filter((_, i) => !afterUsed.has(i));
  const addLeft = [...added];
  const renamed: { b: T; a: T }[] = [];
  const stillRemoved: T[] = [];

  for (const b of removed) {
    const j = addLeft.findIndex((a) => a.kind === b.kind);
    if (j >= 0) {
      renamed.push({ b, a: addLeft[j] });
      addLeft.splice(j, 1);
    } else {
      stillRemoved.push(b);
    }
  }

  const changes: InvoiceLineChange<T>[] = [];
  let editedN = 0;
  for (const pair of [...matched, ...renamed]) {
    if (sameContent(pair.b, pair.a)) continue;
    editedN += 1;
    changes.push({
      id: `edited:${editedN}:${pair.b.kind}`,
      type: 'edited',
      kind: pair.b.kind,
      before: pair.b,
      after: pair.a,
      beforeText: formatInvoiceLineSnapshot(pair.b),
      afterText: formatInvoiceLineSnapshot(pair.a),
    });
  }

  stillRemoved.forEach((b, i) => {
    changes.push({
      id: `removed:${i + 1}:${b.kind}`,
      type: 'removed',
      kind: b.kind,
      before: b,
      after: null,
      beforeText: formatInvoiceLineSnapshot(b),
      afterText: '',
    });
  });

  addLeft.forEach((a, i) => {
    changes.push({
      id: `added:${i + 1}:${a.kind}`,
      type: 'added',
      kind: a.kind,
      before: null,
      after: a,
      beforeText: '',
      afterText: formatInvoiceLineSnapshot(a),
    });
  });

  return changes;
}
